/**
 * DecisionFlow — RetentionQuotaManager
 * ui/src/components/tenant-admin/RetentionQuotaManager.jsx
 *
 * Domain: §P4-05 Retention Quota
 * Embedded in: PlanDetailView modifier card grid (Pattern A)
 * API:
 *   GET  /api/v1/retention-quotas?plan_component_id={id}
 *   POST /api/v1/retention-quotas
 *   POST /api/v1/retention-quotas/{id}/approve
 * Feature gate: Standard — no gate check needed.
 * tenant_super_admin excluded (NN-36).
 * State derived from approved_by / submitted_at — no status column.
 * planComponentId prop is required — all API calls scoped to it.
 * Path: ui/src/components/tenant-admin/RetentionQuotaManager.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function deriveState(entity) {
  if (entity.approved_by)                         return 'approved';
  if (entity.submitted_at || entity.submitted_by) return 'pending_approval';
  return 'draft';
}

function getApiErrorCode(error) { return error?.code || null; }
function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// CSS tokens
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Form defaults
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  retention_period_months: '',
  retention_bonus_pct:     '',
  clawback_months:         '',
  effective_date:          '',
  expiration_date:         '',
};

// ---------------------------------------------------------------------------
// RetentionQuotaManager
// ---------------------------------------------------------------------------

export default function RetentionQuotaManager({
  mode, period, wizardSteps, embedded = false,
  planId, planComponentId, fiscalPeriodId, readOnly = false,
}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.retention')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }

  const [items, setItems]                   = useState([]);
  const [listLoading, setListLoading]       = useState(false);
  const [listError, setListError]           = useState('');
  const [selectedItem, setSelectedItem]     = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm]                     = useState(EMPTY_FORM);
  const [saving, setSaving]                 = useState(false);
  const [formError, setFormError]           = useState('');
  const [actionLoading, setActionLoading]   = useState(false);
  const [actionError, setActionError]       = useState('');
  const [toast, setToast]                   = useState(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  // ---------------------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------------------

  const loadItems = useCallback(async () => {
    if (!user || !planComponentId) return;
    setListLoading(true);
    setListError('');
    try {
      const data = await apiGet(`/api/v1/retention-quotas?plan_component_id=${planComponentId}`);
      setItems(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (error) {
      setListError(getErrorMessage(error, 'Unable to load retention quotas.'));
    } finally {
      setListLoading(false);
    }
  }, [user, planComponentId]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // ---------------------------------------------------------------------------
  // Create
  // ---------------------------------------------------------------------------

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.retention_period_months || !form.retention_bonus_pct) {
      setFormError('Retention period and bonus percentage are required.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await apiPost('/api/v1/retention-quotas', {
        plan_component_id:       planComponentId,
        retention_period_months: parseInt(form.retention_period_months, 10),
        retention_bonus_pct:     parseFloat(form.retention_bonus_pct),
        clawback_months:         form.clawback_months ? parseInt(form.clawback_months, 10) : null,
        effective_date:          form.effective_date || null,
        expiration_date:         form.expiration_date || null,
      });
      setShowCreateForm(false);
      setForm(EMPTY_FORM);
      showToast('Retention quota created.');
      await loadItems();
    } catch (error) {
      setFormError(getErrorMessage(error, 'Failed to create retention quota.'));
    } finally {
      setSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async function handleSubmit() {
    if (!selectedItem) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/retention-quotas/${selectedItem.retention_quota_id}/submit`);
      showToast('Submitted for approval.');
      await loadItems();
      setSelectedItem((prev) => ({ ...prev, submitted_at: new Date().toISOString() }));
    } catch (error) {
      setActionError(getErrorMessage(error, 'Failed to submit for approval.'));
    } finally {
      setActionLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Approve
  // ---------------------------------------------------------------------------

  async function handleApprove() {
    if (!selectedItem) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiPost(`/api/v1/retention-quotas/${selectedItem.retention_quota_id}/approve`);
      showToast('Retention quota approved.');
      await loadItems();
      setSelectedItem((prev) => ({ ...prev, approved_by: user?.user_id || user?.id }));
    } catch (error) {
      const code = getApiErrorCode(error);
      if (code === 'SELF_APPROVAL_NOT_ALLOWED') {
        setActionError('Self-approval is not permitted (NN-10).');
      } else {
        setActionError(getErrorMessage(error, 'Failed to approve.'));
      }
    } finally {
      setActionLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  const currentUserId = user?.user_id || user?.id || null;

  if (!user) {
    return (
      <section className="flex flex-col gap-4">
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication required.
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Detail view
  // ---------------------------------------------------------------------------

  if (selectedItem) {
    const state = deriveState(selectedItem);
    return (
      <section className="flex flex-col gap-4">
        <button type="button" onClick={() => { setSelectedItem(null); setActionError(''); }} className={btnSecondary + ' w-fit'}>
          ← Back to list
        </button>

        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">
              Retention Quota — {selectedItem.retention_period_months}mo
            </h2>
            <StatusPill status={state} />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Retention Period</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.retention_period_months != null ? `${selectedItem.retention_period_months} months` : '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Bonus %</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.retention_bonus_pct != null ? `${selectedItem.retention_bonus_pct}%` : '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Clawback Period</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.clawback_months != null ? `${selectedItem.clawback_months} months` : '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Effective</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.effective_date ?? '—'}
              </p>
            </div>
            <div>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Expires</p>
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
                {selectedItem.expiration_date ?? '—'}
              </p>
            </div>
          </div>

          {actionError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
              {actionError}
            </div>
          )}
        </div>

        {!readOnly && (
          <ApprovalPanel
            variant="retention_quota"
            currentState={state}
            submittedBy={selectedItem.submitted_by}
            currentUserId={currentUserId}
            onSubmit={state === 'draft' ? handleSubmit : null}
            onApprove={state === 'pending_approval' ? handleApprove : null}
            onReject={null}
            approvedBy={selectedItem.approved_by}
            approvedAt={selectedItem.approved_at}
            loading={actionLoading}
          />
        )}
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // List view
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-4">

      {readOnly && (
        <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Plan is approved — configuration is read-only.
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Retention Quotas</h2>
        {!readOnly && (
          <button
            type="button"
            onClick={() => { setShowCreateForm((v) => !v); setFormError(''); setForm(EMPTY_FORM); }}
            className={btnPrimary}
          >
            {showCreateForm ? 'Cancel' : '+ Add Quota'}
          </button>
        )}
      </div>

      {showCreateForm && (
        <form
          onSubmit={handleCreate}
          className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3"
        >
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Retention Quota</p>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className={labelClass}>
              Retention Period (months) <span className="text-[rgb(var(--color-danger))]">*</span>
              <input
                type="number"
                min="1"
                step="1"
                value={form.retention_period_months}
                onChange={(e) => setForm((f) => ({ ...f, retention_period_months: e.target.value }))}
                placeholder="12"
                className={inputClass}
                required
              />
            </label>

            <label className={labelClass}>
              Retention Bonus % <span className="text-[rgb(var(--color-danger))]">*</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.retention_bonus_pct}
                onChange={(e) => setForm((f) => ({ ...f, retention_bonus_pct: e.target.value }))}
                placeholder="0.00"
                className={inputClass}
                required
              />
            </label>

            <label className={labelClass}>
              Clawback Period (months)
              <input
                type="number"
                min="0"
                step="1"
                value={form.clawback_months}
                onChange={(e) => setForm((f) => ({ ...f, clawback_months: e.target.value }))}
                placeholder="0"
                className={inputClass}
              />
            </label>

            <label className={labelClass}>
              Effective Date
              <input
                type="date"
                value={form.effective_date}
                onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))}
                className={inputClass}
              />
            </label>

            <label className={labelClass}>
              Expiration Date
              <input
                type="date"
                value={form.expiration_date}
                onChange={(e) => setForm((f) => ({ ...f, expiration_date: e.target.value }))}
                className={inputClass}
              />
            </label>
          </div>

          {formError && (
            <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
              {formError}
            </div>
          )}

          <div className="flex gap-2">
            <button type="submit" disabled={saving} className={btnPrimary}>
              {saving ? 'Saving…' : 'Create Quota'}
            </button>
            <button
              type="button"
              onClick={() => { setShowCreateForm(false); setFormError(''); setForm(EMPTY_FORM); }}
              className={btnSecondary}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {listLoading && (
        <div className="animate-pulse space-y-2">
          <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
          <div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" />
        </div>
      )}

      {listError && (
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {listError}
        </div>
      )}

      {!listLoading && !listError && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No retention quotas configured</p>
          <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Add a retention quota to configure retention bonuses for this component.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
          <table className="min-w-full border-collapse">
            <thead>
              <tr>
                <th className={thClass}>Retention Period</th>
                <th className={thClass}>Bonus %</th>
                <th className={thClass}>Clawback</th>
                <th className={thClass}>Effective</th>
                <th className={thClass}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.retention_quota_id}
                  className="cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
                  onClick={() => { setSelectedItem(item); setActionError(''); }}
                >
                  <td className={tdClass + ' font-medium'}>
                    {item.retention_period_months != null ? `${item.retention_period_months} months` : '—'}
                  </td>
                  <td className={tdClass}>
                    {item.retention_bonus_pct != null ? `${item.retention_bonus_pct}%` : '—'}
                  </td>
                  <td className={tdClass}>
                    {item.clawback_months != null ? `${item.clawback_months}mo` : '—'}
                  </td>
                  <td className={tdClass}>{item.effective_date ?? '—'}</td>
                  <td className={tdClass}>
                    <StatusPill status={deriveState(item)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-4 right-4 z-50 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] shadow-lg"
        >
          {toast}
        </div>
      )}
    </section>
  );
}
